import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { 
  ArrowLeft, 
  Edit, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Building2,
  User,
  FileText,
  Clock,
  Award,
  Upload,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { hrService } from '@/services/hrService'

// Helper function to safely parse array fields
const parseArrayField = (field) => {
  if (Array.isArray(field)) {
    return field
  }
  if (typeof field === 'string') {
    try {
      const parsed = JSON.parse(field)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

const EmployeeDetail = () => {
  const { id } = useParams()
  const [activeTab, setActiveTab] = useState('overview')
  const [documents, setDocuments] = useState([])
  const [onboardingTasks, setOnboardingTasks] = useState([])
  const [performanceReviews, setPerformanceReviews] = useState([])
  const [employmentHistory, setEmploymentHistory] = useState([])

  // Fetch employee data using React Query
  const { data: employee, isLoading: loading, error } = useQuery(
    ['hr-employee-detail', id],
    () => hrService.getEmployeeById(id),
    {
      enabled: !!id,
      select: (response) => {
        // console.log('EmployeeDetail - Employee API response:', response)
        const emp = response?.data?.data?.employee || response?.data?.employee
        if (emp) {
          // Parse skills and certifications
          emp.skills = parseArrayField(emp.skills)
          emp.certifications = parseArrayField(emp.certifications)
        }
        return emp
      },
      onError: (error) => {
        console.error('Employee detail API error:', error)
        toast.error('Failed to load employee details')
      }
    }
  )

  // Debug current data
  console.log('EmployeeDetail - Current data:', {
    employee,
    loading,
    error: error?.message,
    skills: employee?.skills,
    certifications: employee?.certifications
  })

  useEffect(() => {
    if (activeTab === 'documents') {
      fetchDocuments()
    } else if (activeTab === 'onboarding') {
      fetchOnboardingTasks()
    } else if (activeTab === 'performance') {
      fetchPerformanceReviews()
    } else if (activeTab === 'history') {
      fetchEmploymentHistory()
    }
  }, [activeTab, id])

  const fetchDocuments = async () => {
    try {
      // Mock data for now - would need to implement in hrService
      setDocuments([])
      console.log('Documents feature not yet implemented')
    } catch (error) {
      console.error('Error fetching documents:', error)
    }
  }

  const fetchOnboardingTasks = async () => {
    try {
      // Mock data for now - would need to implement in hrService
      setOnboardingTasks([])
      console.log('Onboarding tasks feature not yet implemented')
    } catch (error) {
      console.error('Error fetching onboarding tasks:', error)
    }
  }

  const fetchPerformanceReviews = async () => {
    try {
      // Mock data for now - would need to implement in hrService
      setPerformanceReviews([])
      console.log('Performance reviews feature not yet implemented')
    } catch (error) {
      console.error('Error fetching performance reviews:', error)
    }
  }

  const fetchEmploymentHistory = async () => {
    try {
      // Mock data for now - would need to implement in hrService
      setEmploymentHistory([])
      console.log('Employment history feature not yet implemented')
    } catch (error) {
      console.error('Error fetching employment history:', error)
    }
  }

  const getStatusBadge = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      terminated: 'bg-red-100 text-red-800',
      on_leave: 'bg-yellow-100 text-yellow-800'
    }
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    )
  }

  const getTaskStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'in_progress':
        return <Clock className="h-5 w-5 text-yellow-500" />
      case 'cancelled':
      case 'skipped':
        return <XCircle className="h-5 w-5 text-red-500" />
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />
    }
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: User },
    { id: 'documents', name: 'Documents', icon: FileText },
    { id: 'onboarding', name: 'Onboarding', icon: CheckCircle },
    { id: 'performance', name: 'Performance', icon: Award },
    { id: 'history', name: 'History', icon: Clock }
  ]

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-4">
              <div className="h-20 w-20 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-48"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Employee not found</h3>
        <p className="text-gray-600 mb-4">The employee you're looking for doesn't exist.</p>
        <Link 
          to="/hr/employees"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Employees
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link 
            to="/hr/employees"
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {employee.User.first_name} {employee.User.last_name}
            </h1>
            <p className="text-gray-600">{employee.position} • {employee.employee_id}</p>
          </div>
        </div>
        <Link
          to={`/hr/employees/${id}/edit`}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-primary-700"
        >
          <Edit className="h-4 w-4" />
          <span>Edit Employee</span>
        </Link>
      </div>

      {/* Employee Summary Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start space-x-6">
          <div className="flex-shrink-0">
            {employee.User.avatar ? (
              <img 
                className="h-20 w-20 rounded-full" 
                src={employee.User.avatar} 
                alt="" 
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-gray-300 flex items-center justify-center">
                <span className="text-xl font-medium text-gray-700">
                  {employee.User.first_name[0]}{employee.User.last_name[0]}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Contact Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-900">
                    <Mail className="h-4 w-4 mr-2 text-gray-400" />
                    {employee.User.email}
                  </div>
                  {employee.User.phone && (
                    <div className="flex items-center text-sm text-gray-900">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      {employee.User.phone}
                    </div>
                  )}
                  {employee.address && (
                    <div className="flex items-center text-sm text-gray-900">
                      <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                      {employee.address}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Employment Details</h3>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-900">
                    <Building2 className="h-4 w-4 mr-2 text-gray-400" />
                    {employee.Department?.name || 'No Department'}
                  </div>
                  <div className="flex items-center text-sm text-gray-900">
                    <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                    Hired: {new Date(employee.hire_date).toLocaleDateString()}
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(employee.employment_status)}
                    <span className="text-xs text-gray-500">
                      {employee.employment_type.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Reporting</h3>
                <div className="space-y-2">
                  {employee.Manager ? (
                    <div className="text-sm text-gray-900">
                      <span className="text-gray-500">Reports to:</span><br />
                      {employee.Manager.User.first_name} {employee.Manager.User.last_name}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No manager assigned</div>
                  )}
                  {employee.Subordinates && employee.Subordinates.length > 0 && (
                    <div className="text-sm text-gray-900">
                      <span className="text-gray-500">Manages:</span><br />
                      {employee.Subordinates.length} direct report{employee.Subordinates.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
                  <div className="space-y-3">
                    {employee.date_of_birth && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Date of Birth:</span>
                        <span className="ml-2 text-sm text-gray-900">
                          {new Date(employee.date_of_birth).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {employee.national_id && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">National ID:</span>
                        <span className="ml-2 text-sm text-gray-900">{employee.national_id}</span>
                      </div>
                    )}
                    {employee.tax_id && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Tax ID:</span>
                        <span className="ml-2 text-sm text-gray-900">{employee.tax_id}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
                  <div className="space-y-3">
                    {employee.emergency_contact_name && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Name:</span>
                        <span className="ml-2 text-sm text-gray-900">{employee.emergency_contact_name}</span>
                      </div>
                    )}
                    {employee.emergency_contact_phone && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Phone:</span>
                        <span className="ml-2 text-sm text-gray-900">{employee.emergency_contact_phone}</span>
                      </div>
                    )}
                    {employee.emergency_contact_relationship && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Relationship:</span>
                        <span className="ml-2 text-sm text-gray-900">{employee.emergency_contact_relationship}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {employee.skills && Array.isArray(employee.skills) && employee.skills.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {employee.skills.map((skill, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                      >
                        {typeof skill === 'string' ? skill : JSON.stringify(skill)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {employee.certifications && Array.isArray(employee.certifications) && employee.certifications.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Certifications</h3>
                  <div className="space-y-2">
                    {employee.certifications.map((cert, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <div className="font-medium text-gray-900">
                          {typeof cert === 'object' ? cert.name : cert}
                        </div>
                        {typeof cert === 'object' && cert.issuer && (
                          <div className="text-sm text-gray-600">Issued by: {cert.issuer}</div>
                        )}
                        {typeof cert === 'object' && cert.date && (
                          <div className="text-sm text-gray-600">Date: {cert.date}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Employee Documents</h3>
                <button className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-primary-700">
                  <Upload className="h-4 w-4" />
                  <span>Upload Document</span>
                </button>
              </div>
              
              {documents.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No documents uploaded yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {documents.map((doc) => (
                    <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{doc.document_name}</h4>
                          <p className="text-sm text-gray-600 capitalize">
                            {doc.document_type.replace('_', ' ')}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Uploaded: {new Date(doc.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <button className="text-primary-600 hover:text-primary-900">
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Onboarding Tab */}
          {activeTab === 'onboarding' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Onboarding Progress</h3>
              
              {onboardingTasks.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No onboarding tasks assigned</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {onboardingTasks.map((task) => (
                    <div key={task.id} className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg">
                      {getTaskStatusIcon(task.status)}
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{task.task_name}</h4>
                        {task.task_description && (
                          <p className="text-sm text-gray-600 mt-1">{task.task_description}</p>
                        )}
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span className="capitalize">{task.status.replace('_', ' ')}</span>
                          {task.due_date && (
                            <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                          )}
                          {task.AssignedTo && (
                            <span>
                              Assigned to: {task.AssignedTo.User.first_name} {task.AssignedTo.User.last_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Performance Reviews</h3>
              
              {performanceReviews.length === 0 ? (
                <div className="text-center py-8">
                  <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No performance reviews yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {performanceReviews.map((review) => (
                    <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900 capitalize">
                            {review.review_type.replace('_', ' ')} Review
                          </h4>
                          <p className="text-sm text-gray-600">
                            {new Date(review.review_period_start).toLocaleDateString()} - {' '}
                            {new Date(review.review_period_end).toLocaleDateString()}
                          </p>
                          {review.Reviewer && (
                            <p className="text-sm text-gray-500">
                              Reviewed by: {review.Reviewer.User.first_name} {review.Reviewer.User.last_name}
                            </p>
                          )}
                        </div>
                        {review.overall_rating && (
                          <div className="text-right">
                            <div className="text-2xl font-bold text-primary-600">
                              {review.overall_rating}/5
                            </div>
                            <div className="text-sm text-gray-500">Overall Rating</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Employment History</h3>
              
              {employmentHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No employment history available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {employmentHistory.map((history) => (
                    <div key={history.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900">{history.position}</h4>
                          <p className="text-sm text-gray-600">
                            {history.Department?.name} • {history.employment_type.replace('_', ' ')}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(history.start_date).toLocaleDateString()} - {' '}
                            {history.end_date ? new Date(history.end_date).toLocaleDateString() : 'Present'}
                          </p>
                          {history.reason_for_change && (
                            <p className="text-sm text-gray-600 mt-2">{history.reason_for_change}</p>
                          )}
                        </div>
                        {history.is_current && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmployeeDetail